import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box, Card, CardContent, Typography, Chip,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TextField, TableSortLabel,
  MenuItem, Select, InputLabel, FormControl, Tabs, Tab,
  LinearProgress, IconButton, Tooltip as MuiTooltip
} from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, CartesianGrid, LabelList
} from "recharts";
import { apiUrl } from "../utils/api";

/* ───────── types ───────── */
interface PMDashboardProps {
  projectId: number | null;
  access?: string;
}
type SortDir = "asc" | "desc";

/* ───────── palette ───────── */
const STATUS_COLORS: Record<string, string> = {
  Approved: "#2e7d32",
  "Pending Approval": "#ed6c02",
  Draft: "#9e9e9e",
  Rejected: "#d32f2f",
};
const COMP_COLORS = ["#1976d2", "#7b1fa2", "#0288d1", "#00796b"];
const BAR_COLORS = { budget: "#1976d2", spent: "#d32f2f", remaining: "#2e7d32" };
const HEALTH_COLORS: Record<string, string> = { green: "#2e7d32", amber: "#ed6c02", red: "#d32f2f" };

/* ───────── helpers ───────── */
const fmtCurrency = (val: number | undefined | null, currency = "SGD") => {
  if (val == null || val === 0) return `${currency} 0.00`;
  return `${currency} ${Number(val).toLocaleString("en-SG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const statusChip = (status: string) => {
  const color = STATUS_COLORS[status] || "#757575";
  return <Chip label={status} size="small" sx={{ bgcolor: color, color: "#fff", fontWeight: 600, fontSize: "0.75rem" }} />;
};

/* ───────── component ───────── */
const PMDashboard: React.FC<PMDashboardProps> = ({ projectId }) => {
  const [data, setData] = useState<any>({});
  const [contractRows, setContractRows] = useState<any[]>([]);
  const [procRows, setProcRows] = useState<any[]>([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [docTypeFilter, setDocTypeFilter] = useState("all");
  const [vendorFilter, setVendorFilter] = useState("");

  // Cross-filter state (Power BI style)
  const [chartStatusFilter, setChartStatusFilter] = useState<string | null>(null);
  const [chartCompFilter, setChartCompFilter] = useState<string | null>(null);
  const [contractIdFilter, setContractIdFilter] = useState<number | null>(null);

  // Table sort & search
  const [cSearch, setCSearch] = useState("");
  const [cSort, setCSort] = useState<{ col: string; dir: SortDir }>({ col: "reference", dir: "asc" });
  const [pSearch, setPSearch] = useState("");
  const [pSort, setPSort] = useState<{ col: string; dir: SortDir }>({ col: "reference", dir: "asc" });

  /* ───────── fetch ───────── */
  const fetchData = useCallback(() => {
    if (!projectId) return;
    setLoading(true);

    const qs = new URLSearchParams();
    qs.set("project_id", String(projectId));
    if (dateFrom) qs.set("from_date", dateFrom);
    if (dateTo) qs.set("to_date", dateTo);

    Promise.all([
      fetch(apiUrl(`/api/pm-dashboard?${qs}`)).then(r => r.json()),
      fetch(apiUrl(`/api/contract-vo-table?${qs}`)).then(r => r.json()),
      fetch(apiUrl(`/api/procurement-table?${qs}`)).then(r => r.json()),
    ])
      .then(([dash, cvo, proc]) => {
        setData(dash || {});
        setContractRows(cvo?.data || []);
        setProcRows(proc?.data || []);
      })
      .catch(() => { setData({}); setContractRows([]); setProcRows([]); })
      .finally(() => setLoading(false));
  }, [projectId, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset cross-filters when project changes
  useEffect(() => {
    setChartStatusFilter(null);
    setChartCompFilter(null);
    setContractIdFilter(null);
  }, [projectId]);

  /* ───────── derived ───────── */
  const vendors = useMemo(() => {
    const s = new Set<string>();
    procRows.forEach(r => { if (r.vendor) s.add(r.vendor); });
    return Array.from(s).sort();
  }, [procRows]);

  const sortFn = (a: any, b: any, col: string, dir: SortDir) => {
    const av = a[col] ?? "";
    const bv = b[col] ?? "";
    if (typeof av === "number") return dir === "asc" ? av - bv : bv - av;
    return dir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  };

  // Apply cross-filters to contract table
  const filteredContracts = useMemo(() =>
    [...contractRows]
      .filter(r => {
        if (cSearch) {
          const q = cSearch.toLowerCase();
          if (!r.reference?.toLowerCase().includes(q) && !r.title?.toLowerCase().includes(q)) return false;
        }
        if (chartStatusFilter && r.status !== chartStatusFilter) return false;
        if (chartCompFilter) {
          if (chartCompFilter === "Contract" && r.type === "variation_order") return false;
          if (chartCompFilter === "VO" && r.type !== "variation_order") return false;
        }
        return true;
      })
      .sort((a, b) => sortFn(a, b, cSort.col, cSort.dir)),
    [contractRows, cSearch, cSort, chartStatusFilter, chartCompFilter]
  );

  // Apply cross-filters to procurement table
  const filteredProc = useMemo(() =>
    [...procRows]
      .filter(r => {
        if (docTypeFilter !== "all" && r.doc_type !== docTypeFilter) return false;
        if (vendorFilter && r.vendor !== vendorFilter) return false;
        if (contractIdFilter && r.contract_id !== contractIdFilter) return false;
        if (chartStatusFilter && r.status !== chartStatusFilter) return false;
        if (chartCompFilter) {
          if (chartCompFilter === "Contract" && r.contract_id == null) return false;
          if (chartCompFilter === "VO" && r.contract_id == null) return false;
        }
        if (pSearch) {
          const q = pSearch.toLowerCase();
          if (!(r.reference?.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q) || r.vendor?.toLowerCase().includes(q))) return false;
        }
        return true;
      })
      .sort((a, b) => sortFn(a, b, pSort.col, pSort.dir)),
    [procRows, pSearch, pSort, docTypeFilter, vendorFilter, contractIdFilter, chartStatusFilter, chartCompFilter]
  );

  if (!projectId) {
    return <Box p={3}><Typography variant="h6" color="text.secondary">Select a project first</Typography></Box>;
  }

  const contract = data.contract || {};
  const vo = data.vo || {};
  const mr = data.mr || {};
  const po = data.po || {};

  const hasAnyCrossFilter = chartStatusFilter || chartCompFilter || contractIdFilter;

  /* ───────── chart click handlers (Power BI style) ───────── */
  const onStatusPieClick = (entry: any) => {
    setChartStatusFilter(prev => prev === entry.name ? null : entry.name);
  };
  const onCompPieClick = (entry: any) => {
    setChartCompFilter(prev => prev === entry.name ? null : entry.name);
  };
  const clearCrossFilters = () => {
    setChartStatusFilter(null);
    setChartCompFilter(null);
    setContractIdFilter(null);
  };

  /* ───────── KPI card helper ───────── */
  const KpiCard = ({ title, main, sub, color }: { title: string; main: string | number; sub?: string; color?: string }) => (
    <Card sx={{ height: "100%", transition: "box-shadow 0.2s", "&:hover": { boxShadow: 4 } }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>{title}</Typography>
        <Typography variant="h5" fontWeight={700} color={color || "text.primary"} sx={{ mt: 0.5 }}>{main}</Typography>
        {sub && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{sub}</Typography>}
      </CardContent>
    </Card>
  );

  const toggleSort = (setter: Function, current: { col: string; dir: SortDir }, col: string) => {
    setter({ col, dir: current.col === col && current.dir === "asc" ? "desc" : "asc" });
  };

  // Budget vs Spent vs Remaining bar data (approved budget only)
  const approvedBudget = data.totalBudget || 0;
  const spentAmt = data.spent || 0;
  const remainingAmt = data.remaining || 0;
  const spentPct = approvedBudget > 0 ? ((spentAmt / approvedBudget) * 100).toFixed(1) : "0.0";
  const remainingPct = approvedBudget > 0 ? ((remainingAmt / approvedBudget) * 100).toFixed(1) : "0.0";
  const budgetBarData = [
    { name: "Budget", value: approvedBudget, fill: BAR_COLORS.budget, pct: "100%" },
    { name: "Spent", value: spentAmt, fill: BAR_COLORS.spent, pct: `${spentPct}%` },
    { name: "Remaining", value: remainingAmt, fill: BAR_COLORS.remaining, pct: `${remainingPct}%` },
  ];

  /* ───────── render ───────── */
  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        {hasAnyCrossFilter && (
          <MuiTooltip title="Clear all chart filters">
            <IconButton onClick={clearCrossFilters} color="warning" size="small">
              <FilterListOffIcon /> <Typography variant="caption" sx={{ ml: 0.5 }}>Clear Filters</Typography>
            </IconButton>
          </MuiTooltip>
        )}
      </Box>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Cross-filter indicator */}
      {hasAnyCrossFilter && (
        <Box mb={2} display="flex" gap={1} flexWrap="wrap">
          {chartStatusFilter && (
            <Chip label={`Status: ${chartStatusFilter}`} onDelete={() => setChartStatusFilter(null)} color="primary" size="small" />
          )}
          {chartCompFilter && (
            <Chip label={`Type: ${chartCompFilter}`} onDelete={() => setChartCompFilter(null)} color="secondary" size="small" />
          )}
          {contractIdFilter && (
            <Chip label={`Contract ID: ${contractIdFilter}`} onDelete={() => setContractIdFilter(null)} color="info" size="small" />
          )}
        </Box>
      )}

      {/* ───── FILTERS ───── */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Filter by date range</Typography>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField label="From Date" type="date" size="small" InputLabelProps={{ shrink: true }}
            value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          <TextField label="To Date" type="date" size="small" InputLabelProps={{ shrink: true }}
            value={dateTo} onChange={e => setDateTo(e.target.value)} />
          {(dateFrom || dateTo) && (
            <Chip label="Clear dates" size="small" onDelete={() => { setDateFrom(""); setDateTo(""); }} />
          )}
        </Box>
      </Paper>

      {/* ───── SECTION A: KPI CARDS ───── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard title="Approved Budget" main={fmtCurrency(data.totalBudget)} color="#1976d2" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard title="Total Spent" main={fmtCurrency(data.spent)} color="#d32f2f" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard title="Remaining Budget" main={fmtCurrency(data.remaining)} color="#2e7d32" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard title="Total Contracts/VOs" main={(data.totalContracts ?? 0) + (data.totalVOs ?? 0)} sub={`Contracts: ${data.totalContracts ?? 0};  VOs: ${data.totalVOs ?? 0}`} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard title="Approved Records" main={data.totalApproved ?? 0} sub={`Contract: ${contract.approved ?? 0};  VO: ${vo.approved ?? 0};  MR: ${mr.approved ?? 0};  PO: ${po.approved ?? 0}`} color="#2e7d32" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard title="Pending Approval" main={data.totalPending ?? 0} sub={`Contract: ${contract.pending ?? 0};  VO: ${vo.pending ?? 0};  MR: ${mr.pending ?? 0};  PO: ${po.pending ?? 0}`} color="#ed6c02" />
        </Grid>
      </Grid>

      {/* Contract+VO / MR / PO breakdown cards */}
      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
        {[
          { label: "Contracts and Variation Orders", d: { approved: (contract.approved ?? 0) + (vo.approved ?? 0), pending: (contract.pending ?? 0) + (vo.pending ?? 0), draft: (contract.draft ?? 0) + (vo.draft ?? 0), rejected: (contract.rejected ?? 0) + (vo.rejected ?? 0) }, extra: `Contracts: ${data.totalContracts ?? 0} | VOs: ${data.totalVOs ?? 0}` },
          { label: "Material Requisitions", d: mr, extra: `With PO: ${mr.with_po ?? 0} | Without PO: ${mr.without_po ?? 0}` },
          { label: "Purchase Orders", d: po, extra: `Total: ${(po.approved ?? 0) + (po.pending ?? 0) + (po.draft ?? 0) + (po.rejected ?? 0)}` },
        ].map(({ label, d, extra }) => (
          <Grid item xs={12} md={4} key={label}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>{label}</Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Chip label={`Approved: ${d.approved ?? 0}`} size="small" sx={{ bgcolor: "#2e7d32", color: "#fff" }} />
                  <Chip label={`Pending: ${d.pending ?? 0}`} size="small" sx={{ bgcolor: "#ed6c02", color: "#fff" }} />
                  <Chip label={`Draft: ${d.draft ?? 0}`} size="small" sx={{ bgcolor: "#9e9e9e", color: "#fff" }} />
                  <Chip label={`Rejected: ${d.rejected ?? 0}`} size="small" sx={{ bgcolor: "#d32f2f", color: "#fff" }} />
                </Box>
                {extra && <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>{extra}</Typography>}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ───── SECTION B: CHARTS ───── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Budget vs Spent vs Remaining */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Budget vs Spent vs Remaining</Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={budgetBarData}>
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(v: number) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : String(v)} />
                <Tooltip formatter={(v: number) => fmtCurrency(v)} />
                <Bar dataKey="value">
                  {budgetBarData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                  <LabelList dataKey="pct" position="top" style={{ fontWeight: 700, fontSize: 12 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Cost Composition (Contracts & VOs only - budget composition) */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, border: chartCompFilter ? "2px solid #7b1fa2" : "none" }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Budget Composition
              {chartCompFilter && <Chip label={chartCompFilter} size="small" sx={{ ml: 1 }} onDelete={() => setChartCompFilter(null)} />}
            </Typography>
            {(() => {
              const compData = (data.costComposition || []).filter((s: any) => s.value > 0.01);
              const compTotal = compData.reduce((sum: number, s: any) => sum + s.value, 0);
              return (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={compData} dataKey="value" nameKey="name"
                        cx="50%" cy="50%" outerRadius={85}
                        label={false}
                        onClick={onCompPieClick} style={{ cursor: "pointer" }}>
                        {compData.map((entry: any, i: number) => (
                          <Cell key={i} fill={COMP_COLORS[i % COMP_COLORS.length]}
                            opacity={chartCompFilter && chartCompFilter !== entry.name ? 0.3 : 1} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: number) => fmtCurrency(v)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 0.5 }}>
                    Total Approved: {fmtCurrency(compTotal)}
                  </Typography>
                </>
              );
            })()}
          </Paper>
        </Grid>

        {/* Monthly Expenses */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Monthly Expenses</Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.monthlyExpenses || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(v: number) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : String(v)} />
                <Tooltip formatter={(v: number) => fmtCurrency(v)} />
                <Bar dataKey="amount" name="Expenses" fill="#1976d2">
                  <LabelList dataKey="amount" position="top" style={{ fontSize: 10 }}
                    formatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}K` : v.toFixed(0)} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Combined Status Pie */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, border: chartStatusFilter ? "2px solid #1976d2" : "none" }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Combined Status (VO+MR+PO)
              {chartStatusFilter && <Chip label={chartStatusFilter} size="small" sx={{ ml: 1 }} onDelete={() => setChartStatusFilter(null)} />}
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={(data.combinedStatus || []).filter((s: any) => s.value > 0)} dataKey="value" nameKey="name"
                  cx="50%" cy="50%" outerRadius={85} label
                  onClick={onStatusPieClick} style={{ cursor: "pointer" }}>
                  {(data.combinedStatus || []).filter((s: any) => s.value > 0).map((s: any, i: number) => (
                    <Cell key={i} fill={STATUS_COLORS[s.name] || "#757575"}
                      opacity={chartStatusFilter && chartStatusFilter !== s.name ? 0.3 : 1} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* ───── SECTION C & D: TABLES ───── */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tab label={`Contracts / VOs (${filteredContracts.length})`} />
          <Tab label={`Procurement (${filteredProc.length})`} />
        </Tabs>

        {/* ═══ Contract / VO Table ═══ */}
        {tab === 0 && (
          <Box p={2}>
            <TextField fullWidth label="Search contracts..." size="small" value={cSearch}
              onChange={e => setCSearch(e.target.value)} sx={{ mb: 2 }} />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    {[
                      { id: "type", label: "Type" },
                      { id: "reference", label: "Reference" },
                      { id: "title", label: "Title" },
                      { id: "status", label: "Status" },
                      { id: "original_value", label: "Original Value" },
                      { id: "approved_value", label: "Approved Budget" },
                      { id: "spent", label: "Spent" },
                      { id: "spent_pct", label: "Spent %" },
                      { id: "remaining", label: "Remaining" },
                      { id: "health", label: "Health" },
                    ].map(col => (
                      <TableCell key={col.id} sx={{ fontWeight: 700 }}>
                        <TableSortLabel active={cSort.col === col.id}
                          direction={cSort.col === col.id ? cSort.dir : "asc"}
                          onClick={() => toggleSort(setCSort, cSort, col.id)}>
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredContracts.length === 0 ? (
                    <TableRow><TableCell colSpan={10} align="center">No data</TableCell></TableRow>
                  ) : filteredContracts.map((r, i) => (
                    <TableRow key={i} hover sx={{ cursor: "pointer", bgcolor: contractIdFilter === r.contract_id ? "#e3f2fd" : "inherit" }}
                      onClick={() => setContractIdFilter(prev => prev === r.contract_id ? null : r.contract_id)}>
                      <TableCell>
                        <Chip label={r.type === "variation_order" ? "VO" : "Contract"} size="small"
                          color={r.type === "variation_order" ? "secondary" : "primary"} variant="outlined" />
                      </TableCell>
                      <TableCell>{r.reference}</TableCell>
                      <TableCell>{r.title}</TableCell>
                      <TableCell>{statusChip(r.status)}</TableCell>
                      <TableCell>{fmtCurrency(r.original_value, r.currency)}</TableCell>
                      <TableCell>{fmtCurrency(r.approved_value, r.currency)}</TableCell>
                      <TableCell>{fmtCurrency(r.spent, r.currency)}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <LinearProgress variant="determinate" value={Math.min(r.spent_pct || 0, 100)}
                            sx={{ width: 50, height: 8, borderRadius: 4,
                              bgcolor: "#e0e0e0",
                              "& .MuiLinearProgress-bar": { bgcolor: r.spent_pct > 95 ? "#d32f2f" : r.spent_pct > 80 ? "#ed6c02" : "#2e7d32" }
                            }} />
                          <Typography variant="caption">{r.spent_pct ?? 0}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{fmtCurrency(r.remaining, r.currency)}</TableCell>
                      <TableCell>
                        <MuiTooltip title={`${r.health} - ${r.remaining > 0 ? "within budget" : "over budget"}`}>
                          <Box sx={{
                            width: 16, height: 16, borderRadius: "50%",
                            bgcolor: HEALTH_COLORS[r.health] || "#9e9e9e",
                            display: "inline-block"
                          }} />
                        </MuiTooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* ═══ Procurement Table ═══ */}
        {tab === 1 && (
          <Box p={2}>
            <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
              <TextField label="Search..." size="small" value={pSearch}
                onChange={e => setPSearch(e.target.value)} sx={{ minWidth: 200 }} />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Doc Type</InputLabel>
                <Select value={docTypeFilter} label="Doc Type" onChange={e => setDocTypeFilter(e.target.value)}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="MR">MR</MenuItem>
                  <MenuItem value="PO">PO</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Vendor</InputLabel>
                <Select value={vendorFilter} label="Vendor" onChange={e => setVendorFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {vendors.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                </Select>
              </FormControl>
              {contractIdFilter && (
                <Chip label={`Linked to Contract #${contractIdFilter}`} onDelete={() => setContractIdFilter(null)} color="info" size="small" />
              )}
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    {[
                      { id: "doc_type", label: "Type" },
                      { id: "reference", label: "Doc Number" },
                      { id: "vendor", label: "Vendor" },
                      { id: "status", label: "Status" },
                      { id: "amount", label: "Amount" },
                      { id: "date", label: "Issue Date" },
                      { id: "approval_date", label: "Approval Date" },
                      { id: "approved_by", label: "Approved By" },
                      { id: "contract_name", label: "Linked Contract/VO" },
                      { id: "has_po", label: "PO Raised?" },
                      { id: "related_mr", label: "Related Ref" },
                    ].map(col => (
                      <TableCell key={col.id} sx={{ fontWeight: 700 }}>
                        <TableSortLabel active={pSort.col === col.id}
                          direction={pSort.col === col.id ? pSort.dir : "asc"}
                          onClick={() => toggleSort(setPSort, pSort, col.id)}>
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProc.length === 0 ? (
                    <TableRow><TableCell colSpan={11} align="center">No data</TableCell></TableRow>
                  ) : filteredProc.map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell>
                        <Chip label={r.doc_type} size="small"
                          color={r.doc_type === "PO" ? "secondary" : "primary"} variant="outlined" />
                      </TableCell>
                      <TableCell>{r.reference}</TableCell>
                      <TableCell>{r.vendor || "-"}</TableCell>
                      <TableCell>{statusChip(r.status)}</TableCell>
                      <TableCell>{fmtCurrency(r.amount, r.currency)}</TableCell>
                      <TableCell>{r.date || "-"}</TableCell>
                      <TableCell>{r.approval_date || "-"}</TableCell>
                      <TableCell>{r.approved_by || "-"}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.contract_name || "-"}
                      </TableCell>
                      <TableCell>
                        {r.doc_type === "MR" ? (
                          r.has_po ? <Chip label="Yes" size="small" color="success" variant="outlined" />
                            : <Chip label="No" size="small" color="error" variant="outlined" />
                        ) : "-"}
                      </TableCell>
                      <TableCell>
                        {r.doc_type === "MR" && r.related_po ? `PO: ${r.related_po}` : ""}
                        {r.doc_type === "PO" && r.related_mr ? `MR: ${r.related_mr}` : ""}
                        {!r.related_po && !r.related_mr ? "-" : ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PMDashboard;
